import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";

const mergeClassNames = (...classes) => classes.filter(Boolean).join(" ");

const resolveLinkComponent = ({ to, href }) => {
    if (to) {
        return { Component: Link, linkProps: { to } };
    }

    if (href) {
        return { Component: "a", linkProps: { href } };
    }

    return { Component: "div", linkProps: {} };
};

export const CHeader = ({
    tag: Tag = "header",
    className = "",
    children,
    withSubheader: _withSubheader,
    colorScheme = "light",
    fixed = true,
    ...rest
}) => {
    const classes = mergeClassNames(
        className,
        "c-header",
        colorScheme ? `c-header-${colorScheme}` : "",
        fixed ? "c-header-fixed" : "",
        _withSubheader ? "c-header-with-subheader" : ""
    );

    return (
        <Tag className={classes} {...rest}>
            {children}
        </Tag>
    );
};

export const CSubheader = ({
    tag: Tag = "div",
    className = "",
    children,
    ...rest
}) => {
    return (
        <Tag className={mergeClassNames("c-subheader", className)} {...rest}>
            {children}
        </Tag>
    );
};

export const CHeaderBrand = ({
    className = "",
    children,
    to,
    href,
    ...rest
}) => {
    const { Component, linkProps } = resolveLinkComponent({ to, href });

    return (
        <Component
            className={mergeClassNames("c-header-brand", className)}
            {...linkProps}
            {...rest}
        >
            {children}
        </Component>
    );
};

export const CHeaderNav = ({ className = "", children, ...rest }) => {
    return (
        <ul className={mergeClassNames("c-header-nav", className)} {...rest}>
            {children}
        </ul>
    );
};

export const CHeaderNavItem = ({ className = "", children, ...rest }) => {
    return (
        <li
            className={mergeClassNames("c-header-nav-item", className)}
            {...rest}
        >
            {children}
        </li>
    );
};

export const CLink = ({
    className = "",
    children,
    to,
    href,
    ...rest
}) => {
    const isNavLink = typeof to !== "undefined";
    const Component = isNavLink ? NavLink : "a";
    const linkProps = isNavLink ? { to } : { href: href || "#" };

    return (
        <Component
            className={className}
            {...linkProps}
            {...rest}
        >
            {children}
        </Component>
    );
};

export const CToggler = ({
    tag: Tag = "button",
    className = "",
    children,
    inHeader: _inHeader,
    inNavbar: _inNavbar,
    ...rest
}) => {
    const type = _inNavbar ? "navbar" : _inHeader ? "c-header" : null;
    const togglerClass = type ? `${type}-toggler` : "";
    const iconClass = type ? `${togglerClass}-icon` : "";
    const typeAttr = Tag === "button" ? { type: "button" } : {};

    return (
        <Tag
            className={mergeClassNames(togglerClass, className)}
            {...typeAttr}
            {...rest}
        >
            {children || <span className={iconClass} />}
        </Tag>
    );
};

export const CSidebar = ({
    className = "",
    children,
    show = "responsive",
    breakpoint = "lg",
    fixed = true,
    unfoldable = false,
    minimize = false,
    size,
    hideOnMobileClick = true,
    aside = false,
    colorScheme = "dark",
    overlaid = false,
    onShowChange,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(show);
    const sidebarRef = useRef(null);

    useEffect(() => {
        setIsOpen(show);
    }, [show]);

    const closeSidebar = () => {
        if (typeof onShowChange === "function") {
            onShowChange(overlaid ? false : "responsive");
        } else {
            setIsOpen(overlaid ? false : "responsive");
        }
    };

    const isOnMobile = () => {
        if (!sidebarRef.current) {
            return false;
        }

        const value = getComputedStyle(sidebarRef.current).getPropertyValue(
            "--is-mobile"
        );

        return Boolean(value && value.trim());
    };

    const isAutoclosable = () => isOnMobile() || overlaid;

    const handleSidebarClick = (event) => {
        const sidebarItemClicked = String(event.target.className).includes(
            "c-sidebar-nav-link"
        );

        if (sidebarItemClicked && hideOnMobileClick && isAutoclosable()) {
            closeSidebar();
        }
    };

    const haveResponsiveClass = breakpoint && isOpen === "responsive";
    const sidebarClassName = mergeClassNames(
        "c-sidebar",
        colorScheme ? `c-sidebar-${colorScheme}` : "",
        isOpen === true ? "c-sidebar-show" : "",
        haveResponsiveClass ? `c-sidebar-${breakpoint}-show` : "",
        fixed && !overlaid ? "c-sidebar-fixed" : "",
        aside ? "c-sidebar-right" : "",
        minimize && !unfoldable ? "c-sidebar-minimized" : "",
        minimize && unfoldable ? "c-sidebar-unfoldable" : "",
        overlaid ? "c-sidebar-overlaid" : "",
        size ? `c-sidebar-${size}` : "",
        className
    );

    return (
        <>
            <aside
                className={sidebarClassName}
                onClick={handleSidebarClick}
                ref={sidebarRef}
                {...rest}
            >
                {children}
            </aside>
            {isOpen === true && (
                <div
                    className="c-sidebar-backdrop c-fade c-show"
                    onClick={closeSidebar}
                />
            )}
        </>
    );
};

export const CSidebarBrand = ({
    className = "",
    children,
    to,
    href,
    ...rest
}) => {
    const { Component, linkProps } = resolveLinkComponent({ to, href });

    return (
        <Component
            className={mergeClassNames("c-sidebar-brand", className)}
            {...linkProps}
            {...rest}
        >
            {children}
        </Component>
    );
};

export const CSidebarNav = ({ className = "", children, ...rest }) => {
    return (
        <ul
            className={mergeClassNames("c-sidebar-nav", "h-100", className)}
            {...rest}
        >
            {children}
        </ul>
    );
};

export const CSidebarNavTitle = ({ className = "", children, ...rest }) => {
    return (
        <li
            className={mergeClassNames("c-sidebar-nav-title", className)}
            {...rest}
        >
            {children}
        </li>
    );
};

export const CSidebarMinimizer = ({
    className = "",
    children,
    ...rest
}) => {
    return (
        <button
            type="button"
            className={mergeClassNames("c-sidebar-minimizer", className)}
            {...rest}
        >
            {children}
        </button>
    );
};
